import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RiCheckboxCircleFill, RiPencilLine } from "react-icons/ri";
import { fetchWorkItemTimeLogs } from "../../Redux/Thunks/projectManagementThunk";
import { parseWorkItemTimeLogsPayload } from "../Projects/projectMappers";
import DateRangePicker from "../../Utility/DatePickerRange/DatePickerRange";
import Typography from "../../Utility/Typography";
import Avatar from "../../Utility/Avatar/Avatar";
import Button from "../../Utility/Button/Button";
import FloatingTooltip from "../../Utility/FloatingTooltip/FloatingTooltip";
import LogTimeModal from "./LogTimeModal/LogTimeModal";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import Loader from "../../Utility/Loader/Loader";
import { formatDateRange, parseDisplayDate, toDateOnlyApiParam } from "../../utils/dateUtils";
import TimeEntryTableTotalsBar from "../TimeEntry/TimeEntryTableTotalsBar";
import {
  formatDurationWithUnit,
  resolveGrandTotalFromMetaOrRows,
} from "../TimeEntry/timeEntryDisplayUtils";
import { initialsFromDisplayName } from "../../utils/userFilterOptions";
import {
  WORK_ITEM_TIME_LOG_SCOPE,
  buildWorkItemTimeLogsQueryParams,
} from "../../utils/workItemTimeLogsQueryParams";
import { getApiErrorMessage, isSilentRequestAbortError } from "../../utils/apiErrorMessage";
import { showError } from "../../utils/toast";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import { APP_ROLE } from "../../permissions/roleKinds";
import { selectAuthUserId } from "../../Redux/Slices/authSlice";
import { canEditWorkItemTimeLog } from "../../utils/workItemTimeLogEditUtils";

const PAGE_SIZE = 5;

const SCOPE_TABS = [
  { id: WORK_ITEM_TIME_LOG_SCOPE.ALL, label: "All" },
  { id: WORK_ITEM_TIME_LOG_SCOPE.MINE, label: "Mine" },
];

function resolveTimeLogsTotalPages(meta, pageSize, page, listLength) {
  const fromMeta = Number(meta?.totalPages ?? meta?.total_pages);
  if (Number.isFinite(fromMeta) && fromMeta >= 1) return Math.floor(fromMeta);

  const total = Number(meta?.total ?? meta?.totalCount ?? meta?.total_count);
  if (Number.isFinite(total) && total >= 0) {
    return Math.max(1, Math.ceil(total / pageSize));
  }

  if (listLength < pageSize) return Math.max(1, page);
  return Math.max(page + 1, 1);
}

/** e.g. "13 May 2026" — matches work log list mockups */
function formatLogListDate(value) {
  const date = parseDisplayDate(value);
  if (!date) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function resolveLoggedByName(item) {
  const by = item?.loggedBy;
  if (!by || typeof by !== "object") return "Unknown";
  const name = String(by.name ?? "").trim();
  if (name) return name;
  return [by.firstName, by.lastName].filter(Boolean).join(" ").trim() || "Unknown";
}

function resolveRowMinutes(item) {
  const n = Number(item?.minutesLogged ?? item?.minutes_logged ?? 0);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n);
}

function isBillableEntry(item) {
  const billableMinutes = Number(item?.billableMinutes ?? item?.billable_minutes ?? 0);
  return (
    item?.isBillable === 1 ||
    item?.isBillable === true ||
    (Number.isFinite(billableMinutes) && billableMinutes > 0)
  );
}

function mapTimeLogToEditEntry(item, { projectId, workItemId, workItemKey, workItemTitle }) {
  const minutes = resolveRowMinutes(item);
  const billableMinutes = Number(item?.billableMinutes ?? item?.billable_minutes ?? minutes);
  return {
    id: item?.id,
    projectId,
    workItemId,
    minutesLogged: minutes,
    billableMinutes: Number.isFinite(billableMinutes) ? billableMinutes : minutes,
    logDate: item?.logDate ?? item?.log_date,
    description: item?.comment ?? "",
    workItemKey: workItemKey ?? "",
    workItemTitle: workItemTitle ?? "",
  };
}

export default function WorkItemTimeLogsSection({
  projectId,
  workItemId,
  workItemKey = "",
  workItemTitle = "",
  refreshKey = 0,
  onRefresh,
  /** When true, omit section heading (rendered inside Activity Worklog tab). */
  embedded = false,
}) {
  const dispatch = useDispatch();
  const { can, roleKind } = usePermissions();
  const currentUserId = useSelector(selectAuthUserId);
  const isAdmin = roleKind === APP_ROLE.ADMIN;
  const canEditTimeEntry = can(PERMISSION_KEYS.TIME_ENTRY_EDIT);

  const [scope, setScope] = useState(WORK_ITEM_TIME_LOG_SCOPE.ALL);
  const [dateRange, setDateRange] = useState(null);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [entryToEdit, setEntryToEdit] = useState(null);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);

  const dateFrom = dateRange?.startDate ? toDateOnlyApiParam(dateRange.startDate) : null;
  const dateTo = dateRange?.endDate ? toDateOnlyApiParam(dateRange.endDate) : null;

  const requestScopeKey = useMemo(
    () =>
      [
        embedded ? "activity-tabs-work-logs" : "detail-work-logs",
        scope,
        dateFrom ?? "",
        dateTo ?? "",
        String(page),
        String(refreshKey),
      ].join(":"),
    [embedded, scope, dateFrom, dateTo, page, refreshKey]
  );

  const refetchLogs = () => {
    setPage(1);
    onRefresh?.();
  };

  useEffect(() => {
    setPage(1);
  }, [projectId, workItemId]);

  useEffect(() => {
    if (!projectId || !workItemId) {
      setRows([]);
      setMeta(null);
      setTotalPages(1);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const queryPreview = buildWorkItemTimeLogsQueryParams({
      scope,
      dateFrom,
      dateTo,
      page,
      limit: PAGE_SIZE,
    });

    dispatch(
      fetchWorkItemTimeLogs({
        projectId,
        workItemId,
        scope: queryPreview.scope,
        dateFrom: queryPreview.dateFrom,
        dateTo: queryPreview.dateTo,
        page: queryPreview.page,
        limit: queryPreview.limit,
        sortBy: queryPreview.sortBy,
        sortOrder: queryPreview.sortOrder,
        requestScope: requestScopeKey,
      })
    )
      .unwrap()
      .then((raw) => {
        if (cancelled) return;
        const { list, meta: nextMeta } = parseWorkItemTimeLogsPayload(raw);
        const nextList = Array.isArray(list) ? list : [];
        setRows(nextList);
        setMeta(nextMeta ?? null);
        setTotalPages(resolveTimeLogsTotalPages(nextMeta, PAGE_SIZE, page, nextList.length));
      })
      .catch((err) => {
        if (cancelled || isSilentRequestAbortError(err)) return;
        setRows([]);
        setMeta(null);
        setTotalPages(1);
        showError(getApiErrorMessage(err, "Failed to load work logs."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, projectId, workItemId, scope, dateFrom, dateTo, page, requestScopeKey]);

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  useEffect(() => {
    if (!entryToEdit) return;
    const row = rows.find((item) => String(item?.id) === String(entryToEdit.id));
    if (
      row &&
      !canEditWorkItemTimeLog({
        canEditTimeEntry,
        isAdmin,
        currentUserId,
        item: row,
      })
    ) {
      setEntryToEdit(null);
    }
  }, [canEditTimeEntry, currentUserId, entryToEdit, isAdmin, rows]);

  const workLogDateRangeLabel = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      return formatDateRange(dateRange.startDate, dateRange.endDate);
    }
    const scopeLabel = scope === WORK_ITEM_TIME_LOG_SCOPE.MINE ? "Mine" : "All";
    return `All dates · ${scopeLabel}`;
  }, [dateRange, scope]);

  const tableTotals = useMemo(
    () => resolveGrandTotalFromMetaOrRows(meta, rows, resolveRowMinutes),
    [meta, rows]
  );

  const tryEditEntry = (item) => {
    if (!canEditTimeEntry) {
      setDeniedPermissionKey(PERMISSION_KEYS.TIME_ENTRY_EDIT);
      return;
    }
    if (
      !canEditWorkItemTimeLog({
        canEditTimeEntry,
        isAdmin,
        currentUserId,
        item,
      })
    ) {
      return;
    }
    setEntryToEdit(
      mapTimeLogToEditEntry(item, { projectId, workItemId, workItemKey, workItemTitle })
    );
  };

  if (!projectId || !workItemId) return null;

  const Root = embedded ? "div" : "section";

  return (
    <Root
      className={`work-item-time-logs-section${embedded ? " work-item-time-logs-section--embedded" : ""}`}
      {...(embedded ? {} : { "aria-labelledby": "work-item-time-logs-heading" })}
    >
      {!embedded ? (
        <Typography variant="h5" component="h2" id="work-item-time-logs-heading" className="!mb-3">
          Work log
        </Typography>
      ) : null}

      <div className="work-item-time-logs-filters">
        <div className="work-item-time-logs-filter work-item-time-logs-filter--range">
          <Typography
            variant="text2"
            component="label"
            className="work-item-time-logs-filter-label"
          >
            Date range
          </Typography>
          <DateRangePicker
            showIcon
            value={dateRange ?? undefined}
            onChange={(range) => {
              setPage(1);
              if (!range?.startDate || !range?.endDate) {
                setDateRange(null);
                return;
              }
              setDateRange(range);
            }}
            placeholder="All dates"
          />
        </div>

        <div className="work-item-time-logs-filter work-item-time-logs-filter--scope">
          <Typography
            variant="text2"
            component="label"
            className="work-item-time-logs-filter-label"
          >
            Scope
          </Typography>
          <div className="work-item-time-logs-scope" role="group" aria-label="Time log scope">
            {SCOPE_TABS.map((tab) => {
              const isActive = scope === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`work-item-time-logs-scope-btn${isActive ? " work-item-time-logs-scope-btn--active" : ""}`}
                  aria-pressed={isActive}
                  onClick={() => {
                    setScope(tab.id);
                    setPage(1);
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {!loading && (meta || rows.length > 0) ? (
        <>
          <TimeEntryTableTotalsBar
            dateRangeLabel={workLogDateRangeLabel}
            totalLabel={tableTotals.totalLabel}
          />
        </>
      ) : null}

      {loading ? (
        <div className="relative min-h-[8rem]">
          <Loader variant="content" label="Loading work logs" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-text-secondary text-sm mt-2">No work logs for this filter.</p>
      ) : (
        <ul className="work-item-time-logs-list">
          {rows.map((item) => {
            const minutes = resolveRowMinutes(item);
            const userName = resolveLoggedByName(item);
            const avatarInitials = initialsFromDisplayName(userName);
            const comment = item?.comment ? String(item.comment).trim() : "";
            const displayComment = comment || "—";
            const billable = isBillableEntry(item);
            const canEditRow = canEditWorkItemTimeLog({
              canEditTimeEntry,
              isAdmin,
              currentUserId,
              item,
            });

            return (
              <li
                key={item?.id ?? `${item?.logDate}-${minutes}-${userName}`}
                className="work-item-time-log-row"
              >
                <span
                  className={`work-item-time-log-status${billable ? " work-item-time-log-status--billable" : ""}`}
                  aria-hidden={!billable}
                  title={billable ? "Billable" : undefined}
                >
                  {billable ? (
                    <RiCheckboxCircleFill className="work-item-time-log-status-icon" />
                  ) : null}
                </span>

                <p className="work-item-time-log-comment">{displayComment}</p>

                <div className="work-item-time-log-meta">
                  <span className="work-item-time-log-duration">
                    {formatDurationWithUnit(minutes)}
                  </span>
                  <span className="work-item-time-log-date">
                    {formatLogListDate(item?.logDate ?? item?.log_date)}
                  </span>
                </div>

                <Avatar
                  name={avatarInitials}
                  colorClass=""
                  className="work-item-time-log-avatar"
                  style={{ backgroundColor: "#00a3bf" }}
                />

                {canEditTimeEntry ? (
                  <div className="work-item-time-log-actions">
                    <FloatingTooltip
                      label={canEditRow ? "Edit" : "You can only edit your own time logs"}
                    >
                      <Button
                        type="button"
                        size="sm"
                        disabled={!canEditRow}
                        className="btn-warning-new !w-7 !h-7 !p-0 flex items-center justify-center border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Edit time log"
                        onClick={() => tryEditEntry(item)}
                      >
                        <RiPencilLine className="text-white text-[12px]" />
                      </Button>
                    </FloatingTooltip>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {!loading && rows.length > 0 && totalPages > 1 ? (
        <div className="work-item-time-logs-pagination flex items-center gap-3 mt-3 text-sm">
          <button
            type="button"
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className="px-2 py-1 border border-border-primary rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-text-secondary">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            className="px-2 py-1 border border-border-primary rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}

      {entryToEdit ? (
        <LogTimeModal
          key={String(entryToEdit.id)}
          isOpen={!!entryToEdit}
          onClose={() => setEntryToEdit(null)}
          issue={null}
          projectId={entryToEdit.projectId}
          workItemId={entryToEdit.workItemId}
          mode="edit"
          timeLogId={entryToEdit.id}
          initialMinutesLogged={entryToEdit.minutesLogged}
          initialBillableMinutes={entryToEdit.billableMinutes}
          initialLogDate={entryToEdit.logDate}
          initialComment={entryToEdit.description}
          initialWorkItemKey={entryToEdit.workItemKey}
          initialWorkItemTitle={entryToEdit.workItemTitle}
          onSuccess={() => {
            setEntryToEdit(null);
            refetchLogs();
          }}
        />
      ) : null}

      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </Root>
  );
}
